import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  User, MapPin, Briefcase, Heart, Link as LinkIcon, 
  Linkedin, ArrowLeft, Loader2, Users, Brain, TrendingUp, 
  Sparkles, Target, Award
} from 'lucide-react';
import IQIBadge from '../components/IQIBadge';
import FollowButton from '../components/FollowButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublicProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/profile/${username}`);
      setProfile(response.data);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading Fountainhead profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Profile Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/community')} className="bg-gradient-to-r from-purple-600 to-pink-600">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Community
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/community')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Community
            </Button>
            <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Fountainhead Profile
            </h1>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      {/* Profile Content */}
      <div className="container mx-auto px-6 py-12 max-w-6xl">
        {/* Profile Header Card */}
        <Card className="mb-8 overflow-hidden">
          <div className="h-32 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600"></div>
          <CardContent className="relative">
            {/* Profile Picture */}
            <div className="absolute -top-16 left-8">
              {profile.profile_picture_url ? (
                <img
                  src={profile.profile_picture_url}
                  alt={profile.name}
                  className="w-32 h-32 rounded-full border-4 border-white shadow-xl object-cover bg-white"
                />
              ) : (
                <div className="w-32 h-32 rounded-full border-4 border-white shadow-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <User className="w-16 h-16 text-white" />
                </div>
              )}
            </div>

            <div className="pt-20 pb-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{profile.name}</h1>
                  <p className="text-lg text-gray-600 mb-1">@{profile.username}</p>
                  <p className="text-sm text-gray-500">{profile.if_user_id}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <div className="flex gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">{profile.follower_count}</div>
                      <div className="text-sm text-gray-600">Followers</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-pink-600">{profile.following_count}</div>
                      <div className="text-sm text-gray-600">Following</div>
                    </div>
                  </div>
                  
                  {/* Follow Button */}
                  <FollowButton 
                    username={profile.username}
                    onFollowChange={(isFollowing) => {
                      // Update follower count in real-time
                      setProfile(prev => ({
                        ...prev,
                        follower_count: prev.follower_count + (isFollowing ? 1 : -1)
                      }));
                    }}
                  />
                </div>
              </div>

              {/* Bio */}
              {profile.bio && (
                <p className="text-gray-700 mt-6 text-lg leading-relaxed">{profile.bio}</p>
              )}

              {/* Mission Statement */}
              {profile.mission_statement && (
                <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-l-4 border-purple-500">
                  <div className="flex items-start gap-3">
                    <Target className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-semibold text-purple-900 mb-1">Mission Statement</div>
                      <p className="text-gray-700 italic">{profile.mission_statement}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Links */}
              <div className="flex flex-wrap gap-4 mt-6">
                {profile.linkedin_url && (
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                    LinkedIn
                  </a>
                )}
                {profile.website_url && (
                  <a
                    href={profile.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors"
                  >
                    <LinkIcon className="w-5 h-5" />
                    Website
                  </a>
                )}
              </div>

              {/* Expertise & Passion Areas */}
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                {profile.expertise && profile.expertise.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Briefcase className="w-5 h-5 text-purple-600" />
                      <h3 className="font-semibold text-gray-900">Expertise</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.expertise.map((item, index) => (
                        <Badge key={index} variant="secondary" className="bg-purple-100 text-purple-700">
                          {item}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {profile.passion_areas && profile.passion_areas.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Heart className="w-5 h-5 text-pink-600" />
                      <h3 className="font-semibold text-gray-900">Passion Areas</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.passion_areas.map((item, index) => (
                        <Badge key={index} variant="secondary" className="bg-pink-100 text-pink-700">
                          {item}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistics Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* IQI Score - Always visible */}
          {profile.avg_iqi !== null && (
            <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Sparkles className="w-5 h-5" />
                  Avg IQI
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold">{profile.avg_iqi}</div>
                <p className="text-sm opacity-90 mt-1">Interaction Quality</p>
              </CardContent>
            </Card>
          )}

          {/* OI Score - Only if opted in */}
          {profile.share_oi && profile.avg_oi !== null && (
            <Card className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Brain className="w-5 h-5" />
                  Avg OI
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold">{profile.avg_oi}</div>
                <p className="text-sm opacity-90 mt-1">Originality Index</p>
              </CardContent>
            </Card>
          )}

          {/* MSA Score - Only if opted in */}
          {profile.share_msa && profile.avg_msa !== null && (
            <Card className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <TrendingUp className="w-5 h-5" />
                  Avg Spark %
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold">{profile.avg_msa}%</div>
                <p className="text-sm opacity-90 mt-1">Innovation Level</p>
              </CardContent>
            </Card>
          )}

          {/* Sessions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-600" />
                Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-gray-900">{profile.completed_sessions}</div>
              <p className="text-sm text-gray-600 mt-1">of {profile.total_sessions} completed</p>
            </CardContent>
          </Card>
        </div>

        {/* Placeholder for future sections */}
        <Card>
          <CardHeader>
            <CardTitle>Public Work & Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12 text-gray-500">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>Public sessions and challenge participation coming soon!</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicProfile;
